import { Head, Link, router, usePage } from "@inertiajs/react"
import { formatErrorMessages, translateErrorMessage } from "@/lib/formErrors"
import { useTableDensity } from "@/lib/useTableDensity"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import HistoryIcon from "@mui/icons-material/History"
import PersonAddIcon from "@mui/icons-material/PersonAdd"
import RefreshIcon from "@mui/icons-material/Refresh"
import RecordHistoryDrawer from "@/pages/history/RecordHistoryDrawer"
import {
    Alert,
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import dayjs, { Dayjs } from "dayjs"
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_PaginationState,
    type MRT_SortingState,
    useMaterialReactTable,
} from "material-react-table"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { IEmployee, Employee } from "@/models/employee"
import { useTranslation } from "react-i18next"

function groupLabel(value: number, t: (k: string) => string) {
    const map: Record<number, string> = {
        0: t("employees.groups.none"),
        1: t("employees.groups.administrative"),
        2: t("employees.groups.operations"),
    }
    return map[value] ?? Employee.Group[value] ?? "—"
}

function departmentLabel(value: number, t: (k: string) => string) {
    const map: Record<number, string> = {
        0: t("employees.departments.none"),
        1: t("employees.departments.administration"),
        2: t("employees.departments.production"),
    }
    return map[value] ?? Employee.Department[value] ?? "—"
}

function positionLabel(value: number, t: (k: string) => string) {
    const map: Record<number, string> = {
        0: t("employees.positions.none"),
        1: t("employees.positions.generalManager"),
        2: t("employees.positions.administrativeAssistant"),
        3: t("employees.positions.operationsSupervisor"),
        4: t("employees.positions.extruderOperator"),
        5: t("employees.positions.flexoOperator"),
        6: t("employees.positions.cutOperator"),
        7: t("employees.positions.concierge"),
        8: t("employees.positions.operator"),
        9: t("employees.positions.receptionist"),
    }
    return map[value] ?? "—"
}

type SortDirection = "asc" | "desc"

type FormData = {
    fpc_id: number | string
    name: string
    last_name: string
    group: number
    department: number
    position: number
    join_date: string
}

const EMPTY_FORM: FormData = {
    fpc_id: "",
    name: "",
    last_name: "",
    group: 0,
    department: 0,
    position: 0,
    join_date: "",
}

function employeeToForm(emp: IEmployee): FormData {
    return {
        fpc_id: emp.fpc_id,
        name: emp.name,
        last_name: emp.last_name,
        group: emp.group,
        department: emp.department,
        position: emp.position,
        join_date: emp.join_date ? dayjs(emp.join_date as unknown as string).format("YYYY-MM-DD") : "",
    }
}

type Props = {
    employees: IEmployee[]
    search: string
    sort: string
    direction: SortDirection
    pagination: { current_page: number; total_pages: number; total_count: number; per_page: number }
    errors?: Record<string, string[]>
}

function EmployeeDialog({ open, employee, onClose, tableParams }: {
    open: boolean
    employee: IEmployee | null
    onClose: () => void
    tableParams: Record<string, string | number>
}) {
    const { t } = useTranslation()
    const isEdit = employee !== null

    const [form, setForm] = useState<FormData>(EMPTY_FORM)
    const [processing, setProcessing] = useState(false)
    const [localErrors, setLocalErrors] = useState<Record<string, string[]>>({})

    useEffect(() => {
        if (open) {
            setForm(isEdit ? employeeToForm(employee) : EMPTY_FORM)
            setLocalErrors({})
        }
    }, [open, employee])

    const set = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | { value: unknown }>) =>
        setForm(prev => ({ ...prev, [field]: e.target.value }))

    const handleSubmit = () => {
        setProcessing(true)
        setLocalErrors({})
        const originalUrl = window.location.pathname + window.location.search
        const query = new URLSearchParams(tableParams as Record<string, string>).toString()
        const url = isEdit
            ? `/employees/${employee.id}?${query}`
            : `/employees?${query}`

        const data = { employee: form }
        const options = {
            preserveScroll: true,
            preserveState: true,
            onFinish: () => setProcessing(false),
            onSuccess: () => onClose(),
            onError: (errors: Record<string, string | string[]>) => {
                window.history.replaceState(window.history.state, "", originalUrl)
                setLocalErrors(
                    Object.fromEntries(
                        Object.entries(errors).map(([k, v]) => [k, Array.isArray(v) ? v : [v]])
                    )
                )
            }
        }

        if (isEdit) router.patch(url, data, options)
        else router.post(url, data, options)
    }

    const errorMessages = formatErrorMessages(t, localErrors)

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? t("employees.form.editEmployee") : t("employees.form.newEmployee")}</DialogTitle>
            <DialogContent dividers>
                {errorMessages.length > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMessages.join(" · ")}
                    </Alert>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
                        <TextField label={t("employees.fpcId")} type="number" size="small" value={form.fpc_id} onChange={set("fpc_id")}
                            error={!!localErrors.fpc_id} helperText={translateErrorMessage(t, localErrors.fpc_id)} />
                        <TextField label={t("employees.fullName")} size="small" value={form.name} onChange={set("name")}
                            error={!!localErrors.name} helperText={translateErrorMessage(t, localErrors.name)} />
                        <TextField label={t("employees.lastName")} size="small" value={form.last_name} onChange={set("last_name")}
                            error={!!localErrors.last_name} helperText={translateErrorMessage(t, localErrors.last_name)} />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
                        <FormControl size="small" error={!!localErrors.group}>
                            <InputLabel>{t("employees.group")}</InputLabel>
                            <Select label={t("employees.group")} value={form.group} onChange={e => setForm(p => ({ ...p, group: Number(e.target.value) }))}>
                                <MenuItem value={0}>{t("employees.groups.none")}</MenuItem>
                                <MenuItem value={1}>{t("employees.groups.administrative")}</MenuItem>
                                <MenuItem value={2}>{t("employees.groups.operations")}</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" error={!!localErrors.department}>
                            <InputLabel>{t("employees.department")}</InputLabel>
                            <Select label={t("employees.department")} value={form.department} onChange={e => setForm(p => ({ ...p, department: Number(e.target.value) }))}>
                                <MenuItem value={0}>{t("employees.departments.none")}</MenuItem>
                                <MenuItem value={1}>{t("employees.departments.administration")}</MenuItem>
                                <MenuItem value={2}>{t("employees.departments.production")}</MenuItem>
                            </Select>
                        </FormControl>
                        <DatePicker
                            label={t("employees.joinDate")}
                            value={form.join_date ? dayjs(form.join_date) : null}
                            onChange={(val: Dayjs | null) => setForm(p => ({ ...p, join_date: val ? val.format("YYYY-MM-DD") : "" }))}
                            format="DD-MM-YYYY"
                            slotProps={{ textField: { size: "small", error: !!localErrors.join_date, helperText: translateErrorMessage(t, localErrors.join_date) } }}
                        />
                    </Box>
                    <FormControl size="small" error={!!localErrors.position}>
                        <InputLabel>{t("employees.position")}</InputLabel>
                        <Select label={t("employees.position")} value={form.position} onChange={e => setForm(p => ({ ...p, position: Number(e.target.value) }))}>
                            <MenuItem value={0}>{t("employees.positions.none")}</MenuItem>
                            <MenuItem value={1}>{t("employees.positions.generalManager")}</MenuItem>
                            <MenuItem value={2}>{t("employees.positions.administrativeAssistant")}</MenuItem>
                            <MenuItem value={9}>{t("employees.positions.receptionist")}</MenuItem>
                            <MenuItem value={3}>{t("employees.positions.operationsSupervisor")}</MenuItem>
                            <MenuItem value={4}>{t("employees.positions.extruderOperator")}</MenuItem>
                            <MenuItem value={5}>{t("employees.positions.flexoOperator")}</MenuItem>
                            <MenuItem value={6}>{t("employees.positions.cutOperator")}</MenuItem>
                            <MenuItem value={8}>{t("employees.positions.operator")}</MenuItem>
                            <MenuItem value={7}>{t("employees.positions.concierge")}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button color="inherit" onClick={onClose} disabled={processing}>{t("common.cancel")}</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={processing}>
                    {processing ? t("common.saving") : t("common.save")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function DeleteDialog({ employee, onClose, tableParams }: {
    employee: IEmployee | null
    onClose: () => void
    tableParams: Record<string, string | number>
}) {
    const { t } = useTranslation()
    const [processing, setProcessing] = useState(false)
    const [snapshot, setSnapshot] = useState<IEmployee | null>(null)

    const handleDelete = () => {
        if (!snapshot) return
        setProcessing(true)
        const query = new URLSearchParams(tableParams as Record<string, string>).toString()
        router.delete(`/employees/${snapshot.id}?${query}`, {
            preserveScroll: true,
            onFinish: () => { setProcessing(false); onClose() },
        })
    }

    return (
        <Dialog
            open={employee !== null}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            slotProps={{ transition: { onEnter: () => setSnapshot(employee) } }}
        >
            <DialogTitle>{t("employees.deleteTitle")}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t("employees.deleteText", { name: snapshot?.full_name })}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button color="inherit" onClick={onClose} disabled={processing}>{t("common.cancel")}</Button>
                <Button color="error" variant="contained" onClick={handleDelete} disabled={processing}>
                    {processing ? t("common.deleting") : t("common.delete")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function DuplicateDialog({ employee, onClose, tableParams }: {
    employee: IEmployee | null
    onClose: () => void
    tableParams: Record<string, string | number>
}) {
    const { t } = useTranslation()
    const [processing, setProcessing] = useState(false)
    const [snapshot, setSnapshot] = useState<IEmployee | null>(null)

    const handleDuplicate = () => {
        if (!snapshot) return
        setProcessing(true)
        const query = new URLSearchParams(tableParams as Record<string, string>).toString()
        router.post(`/employees/${snapshot.id}/duplicate?${query}`, {}, {
            preserveScroll: true,
            onFinish: () => { setProcessing(false); onClose() },
        })
    }

    return (
        <Dialog
            open={employee !== null}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            slotProps={{ transition: { onEnter: () => setSnapshot(employee) } }}
        >
            <DialogTitle>{t("employees.duplicateTitle")}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t("employees.duplicateText", { name: snapshot?.full_name })}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button color="inherit" onClick={onClose} disabled={processing}>{t("common.cancel")}</Button>
                <Button variant="contained" onClick={handleDuplicate} disabled={processing}>
                    {processing ? t("employees.duplicating") : t("common.duplicate")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default function EmployeesIndex() {
    const { employees, search: initialSearch, sort, direction, pagination } = usePage<Props>().props
    const { t } = useTranslation()

    const [dialogOpen, setDialogOpen] = useState(false)
    const [selectedEmployee, setSelectedEmployee] = useState<IEmployee | null>(null)
    const [deleteEmployee, setDeleteEmployee] = useState<IEmployee | null>(null)
    const [duplicateEmployee, setDuplicateEmployee] = useState<IEmployee | null>(null)
    const [historyTarget, setHistoryTarget] = useState<{ type: string; id: number } | null>(null)
    const [reloading, setReloading] = useState(false)
    const { density, onDensityChange } = useTableDensity("employees")
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const [sorting, setSorting] = useState<MRT_SortingState>(
        sort ? [{ id: sort, desc: direction === "desc" }] : []
    )
    const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
        pageIndex: pagination.current_page - 1,
        pageSize: pagination.per_page,
    })
    const [globalFilter, setGlobalFilter] = useState(initialSearch ?? "")

    useEffect(() => { setGlobalFilter(initialSearch ?? "") }, [initialSearch])
    useEffect(() => { setSorting(sort ? [{ id: sort, desc: direction === "desc" }] : []) }, [sort, direction])
    useEffect(() => {
        setPaginationState({ pageIndex: pagination.current_page - 1, pageSize: pagination.per_page })
    }, [pagination.current_page, pagination.per_page])

    const visit = useCallback((params: Record<string, string | number>) => {
        router.get("/employees", params, { preserveState: true, preserveScroll: true })
    }, [])

    const openCreate = () => { setSelectedEmployee(null); setDialogOpen(true) }
    const openEdit = (emp: IEmployee) => { setSelectedEmployee(emp); setDialogOpen(true) }

    const tableParams = useMemo(() => ({
        search: globalFilter,
        sort: sorting[0]?.id ?? "",
        direction: (sorting[0]?.desc ? "desc" : "asc") as SortDirection,
        page: paginationState.pageIndex + 1,
        per_page: paginationState.pageSize,
    }), [globalFilter, sorting, paginationState])

    const columns = useMemo<MRT_ColumnDef<IEmployee>[]>(() => [
        { accessorKey: "formatted_fpc_id", header: t("employees.fpcId"), size: 100 },
        { accessorKey: "full_name", header: t("employees.fullName"), size: 200 },
        {
            accessorKey: "group",
            header: t("employees.group"),
            size: 120,
            enableSorting: false,
            Cell: ({ cell }) => groupLabel(cell.getValue<number>(), t),
        },
        {
            accessorKey: "department",
            header: t("employees.department"),
            size: 140,
            enableSorting: false,
            Cell: ({ cell }) => departmentLabel(cell.getValue<number>(), t),
        },
        {
            accessorKey: "position",
            header: t("employees.position"),
            size: 180,
            enableSorting: false,
            Cell: ({ cell }) => positionLabel(cell.getValue<number>(), t),
        },
        { accessorKey: "formatted_join_date", header: t("employees.joinDate"), size: 120 },
        { accessorKey: "formatted_updated_at", header: t("common.updatedAt"), size: 160 },
    ], [t])

    const table = useMaterialReactTable({
        columns,
        data: employees,
        enableRowActions: true,
        positionActionsColumn: "last",
        enableColumnFilters: false,
        enableFullScreenToggle: false,
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        rowCount: pagination.total_count,
        onGlobalFilterChange: (updater) => {
            const val = typeof updater === "function" ? updater(globalFilter) : (updater ?? "")
            setGlobalFilter(val)
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
                visit({
                    search: val,
                    sort: sorting[0]?.id ?? "",
                    direction: sorting[0]?.desc ? "desc" : "asc",
                    page: 1,
                    per_page: paginationState.pageSize,
                })
            }, 400)
        },
        onPaginationChange: (updater) => {
            const next = typeof updater === "function" ? updater(paginationState) : updater
            setPaginationState(next)
            visit({
                search: globalFilter,
                sort: sorting[0]?.id ?? "",
                direction: sorting[0]?.desc ? "desc" : "asc",
                page: next.pageIndex + 1,
                per_page: next.pageSize,
            })
        },
        onSortingChange: (updater) => {
            const next = typeof updater === "function" ? updater(sorting) : updater
            setSorting(next)
            visit({
                search: globalFilter,
                sort: next[0]?.id ?? "",
                direction: next[0]?.desc ? "desc" : "asc",
                page: 1,
                per_page: paginationState.pageSize,
            })
        },
        muiPaginationProps: {
            rowsPerPageOptions: [25, 50, 100, 200],
            showFirstButton: true,
            showLastButton: true,
        },
        renderRowActions: ({ row }) => (
            <Box sx={{ display: "flex" }}>
                <Tooltip title={t("common.edit")}>
                    <IconButton color="info" size="small" onClick={() => openEdit(row.original)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t("common.duplicate")}>
                    <IconButton color="warning" size="small" onClick={() => setDuplicateEmployee(row.original)}>
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t("history.openRecordHistory")}>
                    <IconButton size="small" onClick={() => setHistoryTarget({ type: "Employee", id: row.original.id })}>
                        <HistoryIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t("common.delete")}>
                    <IconButton color="error" size="small" onClick={() => setDeleteEmployee(row.original)}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
        onDensityChange,
        state: {
            showProgressBars: reloading,
            pagination: paginationState,
            globalFilter,
            sorting,
            density,
        },
    })

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Head title={t("employees.title")} />

            <Box sx={{ mb: 2 }}>
                <Button LinkComponent={Link} href="/" size="small" startIcon={<ArrowBackIcon />} color="inherit">
                    {t("common.home")}
                </Button>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>{t("employees.title")}</Typography>
                    <Tooltip title={t("common.reload")}>
                        <IconButton size="small" onClick={() => { setReloading(true); router.reload({ onFinish: () => setReloading(false) }) }}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Button
                        LinkComponent={Link}
                        href="/history?module=employees"
                        startIcon={<HistoryIcon />}
                        variant="outlined"
                        size="small"
                    >
                        {t("history.openHistory")}
                    </Button>
                    <Button variant="contained" startIcon={<PersonAddIcon />} onClick={openCreate} size="small">
                        {t("employees.new")}
                    </Button>
                </Box>
            </Box>

            <MaterialReactTable table={table} />

            <EmployeeDialog
                open={dialogOpen}
                employee={selectedEmployee}
                onClose={() => setDialogOpen(false)}
                tableParams={tableParams}
            />
            <DeleteDialog
                employee={deleteEmployee}
                onClose={() => setDeleteEmployee(null)}
                tableParams={tableParams}
            />
            <DuplicateDialog
                employee={duplicateEmployee}
                onClose={() => setDuplicateEmployee(null)}
                tableParams={tableParams}
            />
            <RecordHistoryDrawer
                open={historyTarget !== null}
                onClose={() => setHistoryTarget(null)}
                type={historyTarget?.type ?? null}
                id={historyTarget?.id ?? null}
            />
        </Container>
    )
}
